"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LoaderCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { LottiePlayer } from "@/components/LottiePlayer";

type ResumePersonalizationTriggerProps = {
  isPersonalized: boolean;
  isSessionLoading?: boolean;
  isUploading: boolean;
  onClick: () => void;
  className?: string;
  autoExpand?: boolean;
  expandDelayMs?: number;
  expandedDurationMs?: number;
};

function ResumePersonalizationLottie({
  isPersonalized,
}: {
  isPersonalized: boolean;
}) {
  return (
    <span
      aria-hidden
      data-resume-animation
      data-muted={isPersonalized ? "true" : "false"}
      className="block h-full w-full opacity-100 transition-[filter] duration-500"
      style={{
        filter: isPersonalized
          ? "grayscale(1) brightness(1.15) contrast(0.65) opacity(0.8)"
          : "none",
      }}
    >
      <LottiePlayer
        src="/animations/resume-personalization.json"
        autoplay
        loop
        minDevicePixelRatio={3}
        className="pointer-events-none h-full w-full"
      />
    </span>
  );
}

export function ResumePersonalizationTrigger({
  isPersonalized,
  isSessionLoading = false,
  isUploading,
  onClick,
  className = "",
  autoExpand = true,
  expandedDurationMs = 8000,
}: ResumePersonalizationTriggerProps) {
  const t = useTranslations("profile.resumePersonalization");
  const label = isPersonalized ? t("trigger.manage") : t("trigger.personalize");
  const expandedText = t("trigger.findRelevantJobs");

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isSessionLoading) return;

    if (!autoExpand || isPersonalized) {
      setIsExpanded(false);
      return;
    }

    setIsExpanded(true);

    const collapseTimer = setTimeout(() => {
      setIsExpanded(false);
    }, expandedDurationMs);

    return () => {
      clearTimeout(collapseTimer);
    };
  }, [autoExpand, isPersonalized, isSessionLoading, expandedDurationMs]);

  const handleClick = () => {
    setIsExpanded(false);
    onClick();
  };

  return (
    <div className={`relative h-10 w-10 shrink-0 ${className}`}>
      <motion.button
        type="button"
        aria-label={label}
        data-testid="resume-personalization-trigger"
        data-personalized={isPersonalized ? "true" : "false"}
        data-expanded={isExpanded ? "true" : "false"}
        disabled={isUploading}
        onClick={handleClick}
        title={label}
        initial={false}
        animate={{
          width: isExpanded ? "auto" : 40,
        }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 32,
        }}
        className="absolute right-0 top-0 z-10 flex h-10 items-center overflow-hidden rounded-full border border-black bg-white outline-none active:translate-y-0.5 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:cursor-wait"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center p-1.5 pointer-events-none">
          <ResumePersonalizationLottie isPersonalized={isPersonalized} />
        </div>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="whitespace-nowrap pr-3.5 text-xs font-semibold text-[#101828]"
            >
              {expandedText}
            </motion.span>
          )}
        </AnimatePresence>

        {isUploading ? (
          <span className="absolute inset-1 flex items-center justify-center rounded-full bg-white/85">
            <LoaderCircle
              className="h-5 w-5 animate-spin text-black"
              aria-hidden
            />
          </span>
        ) : null}
      </motion.button>
    </div>
  );
}

